import { Component }          from '@angular/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { TranslateService }   from '@ngx-translate/core';
import { UIRouter }           from '@uirouter/core';

import { ConfirmService }                 from '../messages/confirm/confirm.service';
import { MessagesService }                from '../messages/messages.service';
import { SecurityService }                from './security.service';
import { SessionService }                 from '../sessions/session.service';
import { SpamEmailModel }                 from './typings/security.spam-email';
import { SpamProtectionActionsComponent } from './security.spam-protection.actions.component';
import { Table }                          from '../table/types/table';
import { TableService }                   from '../table/table.service';
import { TableQueryParameters }           from '../table/types/table-query-parameters';

@Component({
  selector   : 'wor-spam-protection',
  template: require('./security.spam-protection.component.html')
})
export class SpamProtectionComponent {
  @BlockUI('spamProtectionBlock') block: NgBlockUI;

  blockSpamEmails : boolean               = this.session.systemSettings().block_spam_emails;
  emails          : Array<SpamEmailModel> = [];

  tableSettings : Table = {
    cache: {
      enabled: true,
      key    : this.securityService.spamEmailsTableCacheKey
    },
    columns: [
      {
        classes: 'text-left',
        field  : 'email_address',
        header : {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.EMAIL')
        }
      },
      {
        classes: 'text-left',
        field  : 'reminder_type',
        header : {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.TYPE')
        }
      },
      {
        classes: 'text-left',
        field  : 'subject',
        header : {
          text: this.translateService.instant('GENERAL_SPACE.MESSAGE.SUBJECT')
        }
      },
      // WS-6879: this list spans every company, so a row has to say whose it is. It replaces
      // the Subject Spam Score column, which is permanently empty: the classifier stopped
      // scoring subjects in July 2026 when the subject model was removed from the decision.
      // The name alone does not identify a tenant, because the August 2026 abuse ran 44
      // accounts under one company name, so the subscription id sits beside it. That is also
      // the value the companies list can be searched by, unlike the company uuid.
      {
        classes: 'text-left',
        field  : 'company_name',
        header : {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.COMPANY_NAME')
        }
      },
      {
        classes: 'text-left',
        field  : 'subscription_subscription_id',
        header : {
          text: this.translateService.instant('DASHBOARD_SPACE.ADMIN_DASHBOARD.SUBSCRIPTION_ID')
        }
      },
      {
        classes: 'text-left',
        field  : 'spam_detection_score_body',
        header : {
          text: this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.BODY_SPAM_SCORE')
        }
      },
      {
        classes: 'text-left',
        field  : 'date_sent',
        header : {
          text: this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.DATE_SENT')
        },
        pipe: 'globalDate'
      },
      {
        component: {
          bindings: {
            outputs: {
              onOverrideClick: ( email : SpamEmailModel ) => this.override(email)
            }
          },
          enabled: true,
          factory: SpamProtectionActionsComponent
        },
        sortable: false
      }
    ],
    empty: {
      text: this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.NO_BLOCKED_EMAILS')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.securityService.spamEmailsTableCacheKey).pageSize || 10,
      pageSizes : [ 10, 20, 50, 100 ],
      queryCount: () => this.securityService.getSpamEmailsCount()
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.securityService.spamEmailsTableCacheKey, {
      direction: 'desc',
      field    : 'date'
    })
  };

  constructor (
    private confirmService   : ConfirmService,
    private messagesService  : MessagesService,
    private router           : UIRouter,
    private securityService  : SecurityService,
    private session          : SessionService,
    private tableService     : TableService,
    private translateService : TranslateService
  ) {}

  ngOnInit () : void {
    const cache = this.tableService.getCacheForTable(this.securityService.spamEmailsTableCacheKey);

    this.getEmails({
      direction: cache.sorting.direction,
      field    : cache.sorting.field,
      offset   : cache.paging.offset,
      pageSize : cache.paging.pageSize
    });
  }

  getEmails ( params : TableQueryParameters ) : void {
    this.block.start();

    this.securityService.getSpamEmails(params)
    .then(( emails : Array<SpamEmailModel> ) => {
      this.emails = emails;
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  handleBlockSpamEmailsChange ( event : Event ) : void {
    this.securityService.setSpamBlocking(this.blockSpamEmails)
    .then(() => this.session.refresh())
    .then(() => this.blockSpamEmails
      ? this.messagesService.show(this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.SPAM_BLOCKING_ACTIVATED'), 'success')
      : this.messagesService.show(this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.SPAM_BLOCKING_DEACTIVATED'), 'success'))
    .then(() => this.router.stateService.reload())
    .catch(error=> this.messagesService.error(error));
  }

  override ( email : SpamEmailModel ) : void {
    this.confirmService.generic(this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.CONFIRM_OVERRIDE'))
    .then(() => this.block.start())
    .then(() => this.securityService.overrideSpamEmail(email))
    .then(() => {
      this.messagesService.show(this.translateService.instant('SECURITY_SPACE.SPAM_PROTECTION.OVERRIDE_SUCCESS'), 'success');

      this.router.stateService.reload();
    })
    .catch(error=> this.messagesService.error(error))
    .finally(() => this.block.stop());
  }
}
